import type { Marketplace } from "./types";

/**
 * Canonical marketplace_status code -> display label. One consistent Title
 * Case label per code, even where the source Excel used inconsistent
 * capitalization across marketplaces for the same underlying concept
 * (e.g. Shopee "Ready To Ship" vs Lazada "Ready to Ship" both -> this one
 * "Ready To Ship" label). Not translated (see plan Global Constraints).
 */
export const MARKETPLACE_STATUS_LABEL: Record<string, string> = {
  unpaid: "Unpaid",
  on_hold: "On Hold",
  ready_to_ship: "Ready To Ship",
  pending: "Pending",
  awaiting_shipment: "Awaiting Shipment",
  processed: "Processed",
  packed: "Packed",
  awaiting_collection: "Awaiting Collection",
  retry_to_ship: "Retry To Ship",
  in_transit: "In Transit",
  shipped: "Shipped",
  delivered: "Delivered",
  to_confirm_received: "To Confirm Received",
  completed: "Completed",
  cancelled: "Cancelled",
  // Raw codes the adapters actually store (see BE normalizeStatus per
  // marketplace), which the spec's derived list above does not cover.
  retry_ship: "Retry Ship",
  to_confirm_receive: "To Confirm Receive",
  in_cancel: "In Cancel",
  to_return: "To Return",
  canceled: "Canceled",
  returned: "Returned",
};

/**
 * Ordered list of possible marketplace_status codes per marketplace, used
 * to build the grouped filter dropdown (one <optgroup> per marketplace).
 * Mirrors the raw status vocabulary each backend adapter stores on
 * orders.marketplace_status (internal/marketplace/<mkp>/mapper.go
 * normalizeStatus), lower-cased. Every marketplace's own list is
 * internally unique, so pairing a code with its marketplace always yields
 * an unambiguous filter.
 */
export const MARKETPLACE_STATUS_CODES: Record<Marketplace, string[]> = {
  // Lower-cased raw marketplace codes (BE compares case-insensitively), in
  // lifecycle order. Source: each adapter's normalizeStatus switch.
  shopee: [
    "unpaid",
    "ready_to_ship",
    "processed",
    "retry_ship",
    "shipped",
    "to_confirm_receive",
    "completed",
    "in_cancel",
    "cancelled",
    "to_return",
  ],
  lazada: [
    "unpaid",
    "pending",
    "packed",
    "ready_to_ship",
    "shipped",
    "delivered",
    "canceled",
    "returned",
  ],
  tiktok: [
    "unpaid",
    "on_hold",
    "awaiting_shipment",
    "awaiting_collection",
    "in_transit",
    "delivered",
    "completed",
    "cancelled",
  ],
};
